import { useTheme } from "@emotion/react";
import { billingFocusSchema } from "@ternary/api-lib/analytics/schemas/billingFOCUS";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";
import { DimensionPreferenceEntity } from "@ternary/api-lib/core/types";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Divider from "@ternary/web-ui-lib/components/Divider";
import React from "react";
import { z } from "zod";
import { DimensionValuesMap } from "../../../../api/analytics/useGetDimensionValues";
import Checkbox from "../../../../ui-lib/components/Checkbox";
import { FormField } from "../../../../ui-lib/components/Form";
import NumberInput from "../../../../ui-lib/components/NumberInput";
import SelectCheckbox from "../../../../ui-lib/components/SelectCheckbox";
import copyText from "../../copyText";
import { BillingRuleForm } from "./BillingRuleForm";
import { FilterSection } from "./FilterSection";
import { Form } from "./types";

const applyMarginMetricOptions = [
  {
    label: billingFocusSchema.measures.absoluteCredits.displayName,
    value: billingFocusSchema.measures.absoluteCredits.schemaName,
  },
  {
    label: billingFocusSchema.measures.billedCost.displayName,
    value: billingFocusSchema.measures.billedCost.schemaName,
  },
];

interface Props {
  availableDimensions: Dimension[];
  dimensionPreferences: DimensionPreferenceEntity[];
  dimensionValues: DimensionValuesMap;
  form: Form;
  isLoadingDimensionPreferences: boolean;
  isLoadingDimensionValues: boolean;
  onInteraction: (interaction: BillingRuleForm.Interaction) => void;
}

export function BillingRuleFormApplyMargin(props: Props) {
  const theme = useTheme();

  const { form } = props;

  return (
    <Box
      backgroundColor={theme.panel_backgroundColor}
      borderRadius={`0 0 ${theme.borderRadius_2} ${theme.borderRadius_2}`}
    >
      <Divider margin={0} />
      <Box padding={`${theme.space_md} ${theme.space_md} 0`}>
        <Flex gap={theme.space_xs}>
          <form.Field
            name="metrics"
            validators={{
              onChange: z.array(z.string()),
              onMount: z.array(z.string()),
            }}
            children={(field) => {
              return (
                <FormField
                  label={copyText.operatingMarginInputLabel}
                  marginBottom={theme.space_md}
                  required
                >
                  <SelectCheckbox
                    options={applyMarginMetricOptions}
                    selectedValues={field.state.value}
                    onChange={(selectedValues) =>
                      field.handleChange(selectedValues)
                    }
                  />
                </FormField>
              );
            }}
          />
          <form.Field
            name="value"
            validators={{
              onChange: z.number().min(1).max(100),
              onMount: z.number().min(1).max(100),
            }}
            children={(field) => {
              return (
                <FormField
                  label={copyText.percentageInputLabel}
                  marginBottom={theme.space_md}
                  required
                >
                  <NumberInput
                    formatter="percent-divided"
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                  />
                </FormField>
              );
            }}
          />
        </Flex>
        <form.Field
          name="adjustToList"
          children={(field) => {
            return (
              <Checkbox
                label={copyText.adjustToListInputLabel}
                checked={field.state.value}
                onChange={(event) => field.handleChange(event.target.checked)}
              />
            );
          }}
        />
      </Box>
      <FilterSection
        availableDimensions={props.availableDimensions}
        dimensionPreferences={props.dimensionPreferences}
        dimensionValues={props.dimensionValues}
        form={form}
        isLoadingDimensionPreferences={props.isLoadingDimensionPreferences}
        isLoadingDimensionValues={props.isLoadingDimensionValues}
        onInteraction={props.onInteraction}
      />
    </Box>
  );
}
